import * as React from 'react';
import { ReactNode } from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import {
  getUserPreferencesSubject,
  GridPreferences,
  PrefKey,
  updateUserSettings,
  UserPreferences
} from '../../../store/User';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { TextBlock } from '../../Atoms/Text';
import { CustomViewType, getGridDefsByType } from './helpers';
import { buildProjectScopedName, isReservedAccountWideName } from './viewKey';
import { viewNameTakenMessage } from './viewRenameMessages';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { getState } from '../../../store/GeneralStore';

export interface NewViewModalParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  sourceTab: string;
  type: CustomViewType;
  // On a single project's Related Inventory grid, scopes the saved view to that project.
  projectId?: string;
  /** The grid key the view copies its layout from, when the tab has no key of its own. */
  sourceKey?: string;
  /** Fields of the page's own, shown under the name. */
  children?: ReactNode;
  /** Stored on the view beside its layout. */
  viewFields?: Record<string, unknown>;
  /** Holds Save until the page's own fields are complete. */
  canSave?: boolean;
}

export const NewViewModal = ({
  open,
  setOpen,
  sourceTab,
  type,
  projectId,
  sourceKey = `${type}_grid_${sourceTab}`,
  children,
  viewFields,
  canSave = true
}: NewViewModalParams) => {
  const { setSnack } = useSnackbar();
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({} as UserPreferences);
  const [newName, setNewName] = useState('');
  // As a rename does, so "West " is the name "West" and meets the same checks.
  const name = newName.trim();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const prefSub = getUserPreferencesSubject().subscribe((prefs) => {
      setUserPreferences(prefs);
    });

    return () => {
      if (prefSub) prefSub.unsubscribe();
    };
  }, []);

  const handleSubmitForm = async () => {
    const currentSettings: UserPreferences = JSON.parse(JSON.stringify(userPreferences));
    if (!currentSettings?.content) return;

    // A related-inventory view's key carries its project scope in the name, read back by parsing. An
    // account-wide view named like a project-scoped one would vanish from the account list onto that
    // UUID's card, so reserve the shape here rather than let valid input be reclassified.
    if (isReservedAccountWideName(type, projectId, name)) {
      setSnack({ message: 'That view name is reserved. Please choose a different name.', type: 'error', open: true });
      return;
    }

    const nameForKey = projectId ? buildProjectScopedName(projectId, name) : name;
    // Saving under a name already taken would replace that view, and with it what its reports send.
    if (`${type}_grid_${nameForKey}` in currentSettings.content) {
      setSnack({ message: viewNameTakenMessage(name), type: 'error', open: true });
      return;
    }

    setIsSubmitting(true);
    try {
      // The source tab's layout may not be persisted yet (a race with the grid's own save) or have no
      // default entry (e.g. related_project_assets_grid_All is not seeded) — seed from {} rather than
      // JSON.parse(undefined), which would throw and hard-lock the modal on the spinner.
      const source = currentSettings.content[sourceKey as PrefKey];
      const currentTabTableState = source ? JSON.parse(JSON.stringify(source)) : {};
      // A view is a stamp-free snapshot of what is shown.
      delete currentTabTableState.visibilityChangedAt;
      delete currentTabTableState.visibilitySwitched;
      const newView: GridPreferences = {
        customView: true,
        muiConfig: getGridDefsByType(type),
        disabledFields: [],
        ...currentTabTableState,
        ...viewFields
      };
      // Taken live, as the tab's saved model can leave out the defaults the grid shows.
      const shown = getState().gridRefs[sourceKey]?.current?.exportState?.().columns?.columnVisibilityModel;
      if (shown && newView.muiConfig) {
        newView.muiConfig = {
          ...newView.muiConfig,
          columns: { ...newView.muiConfig.columns, columnVisibilityModel: { ...shown } }
        };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (currentSettings.content as Record<PrefKey, any>)[`${type}_grid_${nameForKey}` as PrefKey] = newView;
      const { data, error } = await updateUserSettings(
        currentSettings,
        `${type}_grid_${nameForKey.replace(/ /g, '_')}`
      );
      if (data) setSnack({ message: 'Your view was created successfully', type: 'success', open: true });
      if (error)
        setSnack({ message: `There was an error creating your view: \n${error.message}`, type: 'error', open: true });
      handleClose();
    } catch {
      setSnack({ message: 'There was an error creating your view.', type: 'error', open: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewName('');
    setOpen(false);
  };

  return (
    <Dialog
      data-testid="new-view-modal"
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      fullWidth>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Save As View
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="space-y-5 justify-center w-full">
          <TextField
            required
            data-testid="new-view-name-input"
            sx={{ width: '100%' }}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="View Name *"
            size="small"
          />
          {children}
        </div>
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          Cancel
        </button>
        <button
          data-testid="submit-new-view"
          disabled={!name || !canSave || isSubmitting}
          onClick={handleSubmitForm}
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          type="submit">
          {isSubmitting ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
